import React, { useEffect, useState } from "react";
import { api } from "../api";
import DelegatePortal from "./DelegatePortal";

// A client who is also someone's verified POA keeps one login (backend:
// routes/delegation.py lets a client hold a grant). After sign-in they pick
// which account to use, once per session; Settings → Switch account changes
// it. "self" = their own portal; a number = the client they're acting for.
// The choice only picks a screen: every POA route checks the grant itself.

const KEY = "mp_acting_for";
type Choice = "self" | number | null;

function readChoice(): Choice {
  try {
    const v = sessionStorage.getItem(KEY);
    if (v === "self") return "self";
    const n = v ? Number(v) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch { return null; }
}
function writeChoice(c: "self" | number) {
  try { sessionStorage.setItem(KEY, String(c)); } catch { /* private mode: asks again next load */ }
}

interface Acting { client_id: number; client_name: string }

function useActingFor(enabled: boolean): Acting[] | null {
  const [list, setList] = useState<Acting[] | null>(enabled ? null : []);
  useEffect(() => {
    if (!enabled) return;
    api.get("/delegate/clients").then((r: any) => setList(Array.isArray(r) ? r : [])).catch(() => setList([]));
  }, [enabled]);
  return list;
}

const card: React.CSSProperties = {
  background: "white", borderRadius: "4px", padding: "20px 24px",
  border: "1px solid #F3F4F6", boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
};
const option: React.CSSProperties = {
  width: "100%", textAlign: "left", padding: "14px 16px", border: "1px solid #E5E7EB", borderRadius: "4px",
  background: "white", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", color: "#111827", marginBottom: "8px",
};

export default function ActingGate({ user, onLogout, children }: { user: any; onLogout: () => void; children: React.ReactNode }) {
  const list = useActingFor(user?.role === "client");
  const [choice, setChoice] = useState<Choice>(readChoice());
  const pick = (c: "self" | number) => { writeChoice(c); setChoice(c); };

  if (!list || list.length === 0) return <>{children}</>;
  if (typeof choice === "number" && list.some(a => a.client_id === choice)) {
    return <DelegatePortal onLogout={onLogout} initialClientId={choice} onExit={() => pick("self")} />;
  }
  if (choice === "self") return <>{children}</>;

  return (
    <div style={{ minHeight: "100vh", background: "#F9FAFB", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <div style={{ ...card, width: "100%", maxWidth: "440px" }}>
        <h1 style={{ margin: "0 0 4px", fontSize: "18px", fontWeight: 800, color: "#111827" }}>Which account?</h1>
        <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#6B7280" }}>You can switch any time in Settings.</p>
        <button style={option} onClick={() => pick("self")}><strong>My account</strong></button>
        {list.map(a => (
          <button key={a.client_id} style={option} onClick={() => pick(a.client_id)}>
            <strong>Act for {a.client_name}</strong>
            <span style={{ display: "block", fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>As their power of attorney</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Settings → Security. Renders nothing unless they act for someone. */
export function SwitchAccountCard() {
  const list = useActingFor(true);
  if (!list || list.length === 0) return null;
  const go = (c: "self" | number) => { writeChoice(c); window.location.reload(); };
  return (
    <div style={card}>
      <h3 style={{ margin: "0 0 4px", fontSize: "15px", fontWeight: 700, color: "#111827" }}>Switch account</h3>
      <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#6B7280" }}>You're the power of attorney for someone, so you can use their account too.</p>
      {list.map(a => (
        <button key={a.client_id} style={option} onClick={() => go(a.client_id)}>Act for {a.client_name}</button>
      ))}
    </div>
  );
}
